import React, { useEffect, useState } from "react";
import {
  createSupportTicket, getMySupportTickets, getAdminSupportTickets, updateSupportTicket, replySupportTicket, formatApiErrorDetail,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LifeBuoy, ChevronRight, Send, Inbox, CheckCircle2, Circle, Clock, User as UserIcon, Reply } from "lucide-react";

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
    status === "resolved"
      ? "bg-emerald-600/15 text-emerald-400 border-emerald-600/30"
      : "bg-amber-600/15 text-amber-400 border-amber-600/30"
  }`}>
    {status === "resolved" ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
    {status === "resolved" ? "Resolved" : "Open"}
  </span>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Support() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const loadMine = () => getMySupportTickets().then((d) => setMine(d.tickets || [])).catch(() => setMine([]));
  const loadInbox = () => getAdminSupportTickets().then(setInbox).catch(() => setInbox(null));

  useEffect(() => {
    loadMine();
    if (isAdmin) loadInbox();
    // eslint-disable-next-line
  }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and a description."); return; }
    setSubmitting(true);
    try {
      await createSupportTicket({ subject, message });
      toast.success("Issue submitted — we'll take a look.");
      setSubject(""); setMessage("");
      loadMine();
      if (isAdmin) loadInbox();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateSupportTicket(id, status);
      loadInbox();
      loadMine();
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) { toast.error("Type a reply first."); return; }
    try {
      await replySupportTicket(id, replyText);
      toast.success("Reply sent — ticket resolved.");
      setReplyFor(null); setReplyText("");
      loadInbox(); loadMine();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> workspace <ChevronRight className="h-3 w-3" /> support
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl" data-testid="support-page">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold"><LifeBuoy className="h-4 w-4" /> Support</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Report an issue</h1>
        <p className="text-zinc-400 mt-2">Hit a bug or stuck somewhere? Tell us what happened and we'll look into it.</p>

        {/* Submit form */}
        <form onSubmit={submit} className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:p-5 space-y-3" data-testid="support-form">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="support-subject"
              placeholder="e.g. CSV import fails on licenses"
              className="mt-1 w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-600"
              maxLength={200}
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">What's going on?</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="support-message"
              rows={5}
              placeholder="Describe the issue, what you expected, and any steps to reproduce it."
              className="mt-1 w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-600 resize-y"
              maxLength={4000}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="support-submit"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-md transition-colors"
          >
            <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit issue"}
          </button>
        </form>

        {/* My tickets */}
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-blue-400" /> My submitted issues</div>
          <div className="mt-3 space-y-2" data-testid="my-tickets">
            {mine === null && <div className="text-[12px] text-zinc-600 font-mono">Loading…</div>}
            {mine && mine.length === 0 && <div className="text-[12px] text-zinc-600 font-mono">You haven't reported any issues yet.</div>}
            {(mine || []).map((t) => (
              <div key={t.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-3.5" data-testid={`my-ticket-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold text-sm text-white">{t.subject}</div>
                  <StatusPill status={t.status} />
                </div>
                <p className="text-[13px] text-zinc-400 mt-1.5 whitespace-pre-wrap">{t.message}</p>
                {t.reply && (
                  <div className="mt-2.5 rounded-md border border-emerald-600/25 bg-emerald-600/5 p-2.5" data-testid={`my-ticket-reply-${t.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold flex items-center gap-1.5"><Reply className="h-3 w-3" /> Reply from support</div>
                    <p className="text-[13px] text-zinc-200 mt-1 whitespace-pre-wrap">{t.reply}</p>
                    <div className="text-[11px] text-zinc-600 font-mono mt-1.5">{fmt(t.replied_at)}</div>
                  </div>
                )}
                <div className="text-[11px] text-zinc-600 font-mono mt-2">{fmt(t.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Admin inbox */}
        {isAdmin && (
          <div className="mt-10">
            <div className="flex items-center gap-2 flex-wrap">
              <Inbox className="h-4 w-4 text-emerald-400" />
              <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Customer inbox — all issues</span>
              {inbox && (
                <span className="text-[11px] text-zinc-500 ml-auto font-mono">
                  {inbox.total} total · <span className="text-amber-400">{inbox.open} open</span>
                </span>
              )}
            </div>
            <div className="mt-3 space-y-2" data-testid="admin-inbox">
              {inbox === null && <div className="text-[12px] text-zinc-600 font-mono">Loading inbox…</div>}
              {inbox && inbox.tickets.length === 0 && <div className="text-[12px] text-zinc-600 font-mono">No customer issues reported yet.</div>}
              {(inbox?.tickets || []).map((t) => (
                <div key={t.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-3.5" data-testid={`inbox-ticket-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-white truncate">{t.subject}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <UserIcon className="h-3 w-3" /> {t.email}
                        {t.workspace && <span className="text-zinc-700">·</span>}
                        {t.workspace && <span className="text-zinc-400">{t.workspace}</span>}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${t.plan === "pro" ? "bg-emerald-600/15 text-emerald-400 border-emerald-600/30" : "bg-blue-600/15 text-blue-400 border-blue-600/30"}`}>{t.plan === "pro" ? "Pro" : "Free"}</span>
                      </div>
                    </div>
                    <StatusPill status={t.status} />
                  </div>
                  <p className="text-[13px] text-zinc-400 mt-2 whitespace-pre-wrap">{t.message}</p>
                  {t.reply && (
                    <div className="mt-2.5 rounded-md border border-emerald-600/25 bg-emerald-600/5 p-2.5" data-testid={`inbox-reply-${t.id}`}>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold flex items-center gap-1.5"><Reply className="h-3 w-3" /> Your reply</div>
                      <p className="text-[13px] text-zinc-200 mt-1 whitespace-pre-wrap">{t.reply}</p>
                    </div>
                  )}
                  {replyFor === t.id && (
                    <div className="mt-3" data-testid={`reply-box-${t.id}`}>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        data-testid={`reply-input-${t.id}`}
                        rows={3}
                        autoFocus
                        placeholder="Type your reply to the customer…"
                        className="w-full rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-600 resize-y"
                        maxLength={4000}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => sendReply(t.id)} data-testid={`send-reply-${t.id}`} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-[12px] px-3 py-1.5 rounded-md transition-colors">
                          <Send className="h-3.5 w-3.5" /> Send reply
                        </button>
                        <button onClick={() => { setReplyFor(null); setReplyText(""); }} className="text-[12px] font-semibold text-zinc-400 hover:text-white transition-colors">Cancel</button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-[11px] text-zinc-600 font-mono">{fmt(t.created_at)}</div>
                    <div className="flex items-center gap-3">
                      {replyFor !== t.id && (
                        <button onClick={() => { setReplyFor(t.id); setReplyText(t.reply || ""); }} data-testid={`reply-${t.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                          <Reply className="h-3.5 w-3.5" /> {t.reply ? "Edit reply" : "Reply"}
                        </button>
                      )}
                      {t.status === "resolved" ? (
                        <button onClick={() => setStatus(t.id, "open")} data-testid={`reopen-${t.id}`} className="text-[12px] font-semibold text-zinc-400 hover:text-white transition-colors">Reopen</button>
                      ) : (
                        <button onClick={() => setStatus(t.id, "resolved")} data-testid={`resolve-${t.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-400 hover:text-emerald-300 transition-colors">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Mark resolved
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
